import React, { useEffect, useMemo, useState } from "react";
import { askQuestion, uploadDocument } from "./api/client";
import AnswerView from "./components/AnswerView";
import AskForm from "./components/AskForm";
import CitationList from "./components/CitationList";
import FeedbackPanel from "./components/FeedbackPanel";
import SessionHistory from "./components/SessionHistory";
import { ToastProvider, useToast } from "./components/ToastProvider";

const SESSION_STORAGE_KEY = "asksomething-history";

function formatBytes(bytes) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

function isTextLike(file) {
  return (
    file.type.startsWith("text/") ||
    file.name.endsWith(".md") ||
    file.name.endsWith(".txt") ||
    file.name.endsWith(".csv") ||
    file.name.endsWith(".json") ||
    file.name.endsWith(".html")
  );
}

function readPreview(file) {
  if (!isTextLike(file)) {
    return Promise.resolve("");
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const content = typeof reader.result === "string" ? reader.result : "";
      resolve(content.slice(0, 1800));
    };
    reader.onerror = () => resolve("");
    reader.readAsText(file);
  });
}

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function AppShell() {
  const [answer, setAnswer] = useState(null);
  const [history, setHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReadingFiles, setIsReadingFiles] = useState(false);
  const pushToast = useToast();

  useEffect(() => {
    const historyRaw = window.localStorage.getItem(SESSION_STORAGE_KEY);

    if (historyRaw) {
      try {
        setHistory(JSON.parse(historyRaw));
      } catch {
        window.localStorage.removeItem(SESSION_STORAGE_KEY);
      }
    }
  }, []);

  const persistHistory = (updater) => {
    setHistory((current) => {
      const nextHistory = typeof updater === "function" ? updater(current) : updater;
      window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(nextHistory));
      return nextHistory;
    });
  };

  const updateAttachments = (updater) => {
    setAttachments((current) => {
      const nextAttachments = typeof updater === "function" ? updater(current) : updater;
      return nextAttachments;
    });
  };

  const handleAttachFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) {
      return;
    }

    setIsReadingFiles(true);
    try {
      const staged = await Promise.all(
        files.map(async (file) => {
          const localPreview = await readPreview(file);
          const fileBase64 = await readBase64(file);
          const upload = await uploadDocument({
            filename: file.name,
            contentType: file.type || "application/octet-stream",
            file: fileBase64
          });
          const preview = upload.preview || localPreview;
          return {
            id: crypto.randomUUID(),
            name: file.name,
            type: file.type || "application/octet-stream",
            sizeLabel: formatBytes(file.size),
            preview,
            detectedLanguage: upload.detectedLanguage,
            languageConfidence: upload.languageConfidence,
            uploaded: true,
            documentId: upload.documentId,
            bucket: upload.bucket,
            s3Key: upload.s3Key
          };
        })
      );

      updateAttachments(staged.slice(0, 6));
      pushToast({
        tone: "success",
        title: "Documents ready",
        message: `${staged.length} file${staged.length > 1 ? "s" : ""} attached for the current question. Previous attachments were replaced.`
      });
    } catch (error) {
      pushToast({
        tone: "error",
        title: "Attachment failed",
        message: error.message,
        copyText: error.stack ?? error.message
      });
    } finally {
      setIsReadingFiles(false);
    }
  };

  const removeAttachment = (id) => {
    updateAttachments((current) => current.filter((attachment) => attachment.id !== id));
  };

  const clearAttachments = () => {
    updateAttachments([]);
  };

  const handleAsk = async ({ question }) => {
    setIsLoading(true);
    try {
      const sessionId = crypto.randomUUID();
      const result = await askQuestion({
        question,
        session_id: sessionId,
        attachments: attachments.map(({
          name,
          type,
          sizeLabel,
          preview,
          detectedLanguage,
          languageConfidence,
          documentId,
          bucket,
          s3Key
        }) => ({
          name,
          type,
          sizeLabel,
          preview,
          detectedLanguage,
          languageConfidence,
          documentId,
          bucket,
          s3Key
        }))
      });
      setAnswer(result);
      persistHistory((current) =>
        [
          {
            id: crypto.randomUUID(),
            question,
            attachmentCount: attachments.length
          },
          ...current
        ].slice(0, 6)
      );
    } catch (error) {
      pushToast({
        tone: "error",
        title: "Query failed",
        message: error.message,
        copyText: error.stack ?? error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const introStats = useMemo(
    () => [
      { label: "API", value: "Bedrock + DynamoDB" },
      { label: "Deploy", value: "Canary-ready Lambda" },
      { label: "Uploads", value: "Temporary per question" }
    ],
    []
  );

  return (
    <div className="page-shell">
      <header className="hero">
        <nav className="topbar" aria-label="Primary">
          <div className="brand-lockup">
            <span className="brand-badge">asksomething</span>
            <span className="brand-note">Operational RAG over the Kuiper corpus</span>
          </div>
          <div className="topbar-actions">
            <a className="ghost-button" href="#feedback">
              Send feedback
            </a>
          </div>
        </nav>

        <div className="hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">Serverless, visible, and built to be operated</p>
            <h1>Ask the corpus. Attach documents. Keep the workflow clean on mobile.</h1>
            <p className="hero-text">
              asksomething is the operating layer for Kuiper&apos;s knowledge base: query with
              retrieval, keep evidence visible, stage documents from the browser, and review what
              was asked without losing context.
            </p>

            <div className="hero-actions">
              <a className="primary-link" href="#ask-workspace">
                Start asking
              </a>
              <a className="secondary-link" href="#how-it-works">
                See how it works
              </a>
            </div>

            <dl className="hero-stats" aria-label="System summary">
              {introStats.map((stat) => (
                <div key={stat.label} className="hero-stat">
                  <dt>{stat.label}</dt>
                  <dd>{stat.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <aside className="hero-panel">
            <div className="hero-panel-head">
              <span className="panel-label">Workspace state</span>
              <span className="panel-chip">{attachments.length} attachments</span>
            </div>
            <p className="hero-panel-title">Documents attached for the next question</p>
            <ul className="hero-panel-list">
              {attachments.length === 0 ? (
                <li>
                  Attach PDFs, notes, or source exports for the current browser session.
                </li>
              ) : (
                attachments.map((attachment) => (
                  <li key={attachment.id}>
                    <strong>{attachment.name}</strong>
                    <span>
                      {attachment.type} · {attachment.sizeLabel}
                      {attachment.documentId ? ` · ${attachment.documentId}` : ""}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </aside>
        </div>
      </header>

      <main className="content-grid">
        <section className="workspace" id="ask-workspace">
          <AskForm
            onSubmit={handleAsk}
            isLoading={isLoading}
            isReadingFiles={isReadingFiles}
            onAttachFiles={handleAttachFiles}
            attachments={attachments}
            onRemoveAttachment={removeAttachment}
            onClearAttachments={clearAttachments}
            defaultQuestion="What does this architecture optimize for?"
          />
        </section>

        <section className="support-grid">
          <article className="info-panel" id="how-it-works">
            <div className="section-kicker">How it works</div>
            <h2>Retrieval first, answer second</h2>
            <ol className="numbered-list">
              <li>Documents are attached for the current question and removed from the page when cleared.</li>
              <li>The question is sent to the API with attachment metadata.</li>
              <li>Lambda retrieves from Kuiper&apos;s knowledge base and asks Bedrock for an answer.</li>
              <li>The response returns citations, latency, and model information for inspection.</li>
            </ol>
          </article>

          <article className="info-panel">
            <div className="section-kicker">Design choices</div>
            <h2>Why the UI is shaped this way</h2>
            <ul className="choice-list">
              <li>Light surface with colored accents instead of a heavy black dashboard.</li>
              <li>Single-column mobile flow that collapses cleanly under 960px.</li>
              <li>Reusable toast layer for errors, with copy-to-clipboard for debugging.</li>
              <li>Technical copy over marketing copy so the page reads like an operating surface.</li>
            </ul>
          </article>
        </section>

        <AnswerView answer={answer} />
        <CitationList citations={answer?.citations ?? []} />
        <SessionHistory history={history} />
        <FeedbackPanel />
      </main>
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AppShell />
    </ToastProvider>
  );
}
